// Status and category glyphs (DESIGN_SPEC §8, §9). Status is always glyph + word; category is shape + word.
//   <VerdictGlyph v />                      10px ordinal circle: ring benign · half suspicious · disc malicious · dashed pending
//   <Verdict v score? why? label? bare? />   glyph + CAPS word (+ tabular score, + faint why note); bare = glyph + sr-only word
//   <Sev level label? />                     4-bar glyph, level 1..5 = info..critical; label={false} → glyph + sr-only name
//   <JobState s note? label? ring? batchId? />  queued ◌ · running-ish pip · completed ◌ · failed × · cancelled ×
//   <CatMark c label? hollow? />             category shape; decorative unless `label`; hollow = outline (unpressed legend chip)
//   <Pip state? batchId? />                  live (rings once per mount; change batchId to replay) | still | idle | lost
//   <Slots total filled /> · <SlotBar total filled />   fleet squares / node slot bar (filled = busy)
//   <Arrow />                                the button arrow (SVG; never type the glyph)
//   verdictForScore(score), sevName(level), VERDICT_COLOR, CATEGORIES (legend order, from lib/derive), SEV_NAMES
import type { ReactNode } from "react";
import type { Category } from "@/lib/derive/types";
import { cx } from "./cx";

export type { Category };
export { CATEGORIES } from "@/lib/derive/categories";
export type VerdictLabel = "benign" | "suspicious" | "malicious" | "pending";
export type SevLevel = 1 | 2 | 3 | 4 | 5;
export type SevName = "info" | "low" | "medium" | "high" | "critical";
export type JobStateName =
  | "queued"
  | "dispatched"
  | "provisioning"
  | "booting"
  | "running"
  | "collecting"
  | "completed"
  | "failed"
  | "cancelled";
type PipState = "live" | "still" | "idle" | "lost";

const SEV_NAMES: readonly SevName[] = ["info", "low", "medium", "high", "critical"];
/** Verdict → its status colour token (for --vc on meters and lens numbers). */
export const VERDICT_COLOR: Record<VerdictLabel, string> = {
  benign: "var(--s-benign)",
  suspicious: "var(--s-medium)",
  malicious: "var(--s-critical)",
  pending: "var(--s-pending)",
};

const sevName = (level: SevLevel): SevName => SEV_NAMES[level - 1];

/** computeVerdict's buckets: benign 0–24, suspicious 25–59, malicious 60–100. */
export function verdictForScore(score: number): Exclude<VerdictLabel, "pending"> {
  return score >= 60 ? "malicious" : score >= 25 ? "suspicious" : "benign";
}

export function VerdictGlyph({ v }: { v: VerdictLabel }) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true">
      {v === "malicious" ? (
        <circle cx="5" cy="5" r="5" fill="currentColor" />
      ) : v === "pending" ? (
        <circle cx="5" cy="5" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.25" strokeDasharray="2 1.6" />
      ) : (
        <>
          <circle cx="5" cy="5" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          {v === "suspicious" && <path d="M5 .75A4.25 4.25 0 0 0 5 9.25Z" fill="currentColor" />}
        </>
      )}
    </svg>
  );
}

export function Verdict({
  v,
  score,
  why,
  label,
  bare,
  className,
}: {
  v: VerdictLabel;
  score?: number | null;
  why?: ReactNode;
  label?: string;
  /** glyph only (in its status colour); the word stays for screen readers */
  bare?: boolean;
  className?: string;
}) {
  return (
    <span className={cx("v", `v--${v}`, className)}>
      <VerdictGlyph v={v} />
      {bare ? <span className="sr-only">{label ?? v}</span> : (label ?? v)}
      {score !== undefined && score !== null && <span className="sc">{score}</span>}
      {/* the note may wrap in a narrow lens; the glyph and word never do */}
      {why && <span className="why min-w-0 whitespace-normal leading-snug">{why}</span>}
    </span>
  );
}

export function Sev({ level, label = true, className }: { level: SevLevel; label?: boolean; className?: string }) {
  const name = sevName(level);
  return (
    <span className={cx("sev", name, `l${level - 1}`, className)}>
      <svg viewBox="0 0 11 9" aria-hidden="true">
        <rect x="0" y="6" width="2" height="3" />
        <rect x="3" y="4" width="2" height="5" />
        <rect x="6" y="2" width="2" height="7" />
        <rect x="9" y="0" width="2" height="9" />
      </svg>
      {label ? name : <span className="sr-only">{name}</span>}
    </span>
  );
}

const LIVE_STATES = new Set<JobStateName>(["dispatched", "provisioning", "booting", "running", "collecting"]);

export function JobState({
  s,
  note,
  label,
  ring = false,
  batchId,
  className,
}: {
  s: JobStateName;
  note?: ReactNode;
  label?: string;
  ring?: boolean;
  batchId?: string | number;
  className?: string;
}) {
  const live = LIVE_STATES.has(s);
  const word = label ?? (live ? "running" : s);
  return (
    <span className={cx("st", live && "st--live", s === "cancelled" && "st--cancelled", className)}>
      {(s === "queued" || s === "completed") && <VerdictGlyph v="pending" />}
      {live && <Pip state={ring ? "live" : "still"} batchId={batchId} />}
      {(s === "failed" || s === "cancelled") && (
        <span className="x" aria-hidden="true">
          ×
        </span>
      )}
      {word}
      {note && <span className="note-r">{note}</span>}
    </span>
  );
}

// Outline versions of the clip-path / mask shapes: an inset box-shadow cannot draw them hollow.
const HOLLOW: Partial<Record<Category, { cls: string; w: number; h: number; box: string; d: string; sw: number }>> = {
  registry: { cls: "text-cat-registry", w: 8, h: 8, box: "0 0 8 8", d: "M4 .75 7.25 4 4 7.25.75 4Z", sw: 1.1 },
  memory: { cls: "text-cat-memory", w: 8, h: 7, box: "0 0 8 7", d: "M4 .95 7.3 6.45H.7Z", sw: 1.1 },
  detection: { cls: "text-cat-detection", w: 9, h: 9, box: "0 0 10 10", d: "M5 .9v8.2M1.45 2.95l7.1 4.1M8.55 2.95l-7.1 4.1", sw: 0.9 },
};

export function CatMark({ c, label, hollow }: { c: Category; label?: string; hollow?: boolean }) {
  const a11y = label ? { role: "img", "aria-label": label } : { "aria-hidden": true as const };
  const h = hollow ? HOLLOW[c] : undefined;
  if (h)
    return (
      <svg className={cx("inline-block shrink-0", h.cls)} style={{ verticalAlign: ".02em" }} width={h.w} height={h.h} viewBox={h.box} {...a11y}>
        {label && <title>{label}</title>}
        <path d={h.d} fill="none" stroke="currentColor" strokeWidth={h.sw} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  return <i className={`cm ${c}`} title={label} {...a11y} />;
}

export function Pip({ state = "live", batchId, className }: { state?: PipState; batchId?: string | number; className?: string }) {
  // A new key remounts the span, which replays the one-shot ring (never a loop).
  return <span key={batchId} className={cx("pip", state !== "live" && `pip--${state}`, className)} aria-hidden="true" />;
}

export function Slots({ total, filled }: { total: number; filled: number }) {
  return (
    <span className="slots" aria-hidden="true">
      {Array.from({ length: Math.max(0, total) }, (_, i) => (
        <i key={i} className={i < filled ? "on" : undefined} />
      ))}
    </span>
  );
}

export function SlotBar({ total, filled, className }: { total: number; filled: number; className?: string }) {
  return (
    <div className={cx("slotbar", className)} aria-hidden="true">
      {Array.from({ length: Math.max(0, total) }, (_, i) => (
        <i key={i} className={i < filled ? "on" : undefined} />
      ))}
    </div>
  );
}

export function Arrow() {
  return (
    <svg className="arrow" viewBox="0 0 12 8" aria-hidden="true">
      <path d="M0 4h10.5M7.5 1l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
